import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { EmptyState } from "@/components/ui/empty-state";
import { BookOpen } from "lucide-react";
import {
  useKnowledgeItems,
  useDeleteKnowledgeItem,
  KnowledgeItemType,
} from "@/hooks/knowledge/useKnowledgeItems";
import { useCombinedAgentList } from "@/hooks/agent/useCombinedAgentList";
import AddKnowledgeDropdown from "@/components/knowledge/AddKnowledgeDropdown";
import KnowledgeSection from "@/components/knowledge/KnowledgeSection";
import { useToast } from "@/hooks/use-toast";
import PageHeader from "@/components/layout/PageHeader";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton, SkeletonText, SkeletonCard } from "@/components/ui/skeleton";
import { FileText, File, Link as LinkIcon, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useKnowledge } from "@/contexts/KnowledgeContext";

type FilterType = "all" | "links" | "files" | "snippets";

const KnowledgeBase = () => {
  const { organization } = useAuth();
  const { toast } = useToast();

  // Use KnowledgeContext instead of local state
  const {
    items,
    agents,
    isLoading,
    agentsLoading,
    error,
    isInitialized,
    orgFilter,
    agentFilter,
    openSections,
    setOrgFilter,
    setAgentFilter,
    setOpenSections,
    toggleSection,
    getFilteredItems,
    getTotalItemsCount,
    refreshKnowledge,
    updateItem: contextUpdateItem,
    deleteItem: contextDeleteItem,
  } = useKnowledge();

  // Only need the delete hook for actual deletion
  const { deleteItem: deleteItemHook } = useDeleteKnowledgeItem();

  // Get filtered data from context
  const { orgItems, agentItems, agentsWithItems, agentItemsGrouped } =
    getFilteredItems();

  // Calculate total count from context
  const totalKnowledgeItemsCount = getTotalItemsCount();

  const handleAddSuccess = () => {
    refreshKnowledge();
    toast({
      title: "Knowledge added",
      description: "Your knowledge item has been added successfully",
    });
  };

  const handleDelete = async (id: string, type: string, filePath?: string) => {
    const success = await deleteItemHook(
      id,
      type as KnowledgeItemType,
      filePath,
    );
    if (success) {
      // Remove from context state immediately
      contextDeleteItem(id);
      // Optionally refetch to sync with server
      refreshKnowledge();
      toast({
        title: "Item deleted",
        description: "The knowledge item has been deleted successfully",
      });
    }
    return success;
  };

  const isOpen = (sectionId: string) => openSections.includes(sectionId);

  if (!organization) {
    return (
      <MainLayout>
        <PageHeader title="Knowledge Base" />
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-6">
            <EmptyState
              icon={BookOpen}
              title="Organization Required"
              description="You need to set up an organization before you can access the Knowledge Base."
            />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageHeader title="Knowledge Base" />
      <div className="flex-1 overflow-auto">
        <div className="container mx-auto py-6 max-w-[68rem]">
          <div className="space-y-6">
            {/* Header Row - Keep visible */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Knowledge items
                  {!isLoading && !agentsLoading && totalKnowledgeItemsCount > 0
                    ? ` (${totalKnowledgeItemsCount})`
                    : ""}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Train your agents on files, websites or text snippets
                </p>
              </div>
              <AddKnowledgeDropdown onSuccess={handleAddSuccess} />
            </div>

            {/* Loading state with skeleton cards for knowledge items */}
            {isLoading || agentsLoading ? (
              <div className="space-y-6">
                {/* Organization knowledge section skeleton */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="text-left">
                        <h3 className="text-base font-medium">
                          Organization knowledge
                        </h3>
                        <p className="text-sm text-muted-foreground font-normal">
                          Accessible to all agents in your organization
                        </p>
                      </div>
                    </div>
                    <Skeleton className="h-8 w-64" />
                  </div>

                  {/* Skeleton knowledge cards */}
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <SkeletonCard
                        key={index}
                        showAvatar={false}
                        showDescription={true}
                        descriptionLines={1}
                        showActions={true}
                        actionsCount={3}
                        padding="md"
                      />
                    ))}
                  </div>
                </div>

                {/* Agent-specific knowledge section skeleton */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="text-left">
                        <h3 className="text-base font-medium">
                          Agent-specific knowledge
                        </h3>
                        <p className="text-sm text-muted-foreground font-normal">
                          Knowledge items assigned to specific agents
                        </p>
                      </div>
                    </div>
                    <Skeleton className="h-8 w-64" />
                  </div>

                  <div className="space-y-6">
                    {Array.from({ length: 2 }).map((_, agentIndex) => (
                      <div key={agentIndex} className="space-y-3">
                        <SkeletonText width="1/3" height="sm" />
                        <div className="space-y-3">
                          {Array.from({ length: 2 }).map((_, itemIndex) => (
                            <SkeletonCard
                              key={itemIndex}
                              showAvatar={false}
                              showDescription={true}
                              descriptionLines={1}
                              showActions={true}
                              actionsCount={3}
                              padding="md"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* Actual content when loaded */
              <>
                {/* Accordion Sections */}
                <Accordion
                  type="multiple"
                  value={openSections}
                  onValueChange={setOpenSections}
                  className="w-full"
                >
                  {/* Organization knowledge section */}
                  <AccordionItem
                    value="organization-knowledge"
                    className="border-0"
                  >
                    <div
                      className="flex items-center justify-between py-4 cursor-pointer"
                      onClick={() => toggleSection("organization-knowledge")}
                    >
                      <div className="flex items-center gap-3">
                        <ChevronRight
                          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                            isOpen("organization-knowledge") ? "rotate-90" : ""
                          }`}
                        />
                        <div className="text-left">
                          <h3 className="text-base font-medium">
                            Organization knowledge
                          </h3>
                          <p className="text-sm text-muted-foreground font-normal">
                            Accessible to all agents in your organization
                          </p>
                        </div>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Tabs
                          value={orgFilter}
                          onValueChange={(value) =>
                            setOrgFilter(value as FilterType)
                          }
                        >
                          <TabsList className="h-8">
                            <TabsTrigger
                              value="all"
                              className="text-xs px-2 py-1"
                            >
                              <BookOpen className="mr-1 h-3 w-3" />
                              All ({orgItems.length})
                            </TabsTrigger>
                            <TabsTrigger
                              value="links"
                              className="text-xs px-2 py-1"
                            >
                              <LinkIcon className="mr-1 h-3 w-3" />
                              Links (
                              {
                                orgItems.filter((item) => item.type === "link")
                                  .length
                              }
                              )
                            </TabsTrigger>
                            <TabsTrigger
                              value="files"
                              className="text-xs px-2 py-1"
                            >
                              <File className="mr-1 h-3 w-3" />
                              Files (
                              {
                                orgItems.filter((item) => item.type === "file")
                                  .length
                              }
                              )
                            </TabsTrigger>
                            <TabsTrigger
                              value="snippets"
                              className="text-xs px-2 py-1"
                            >
                              <FileText className="mr-1 h-3 w-3" />
                              Snippets (
                              {
                                orgItems.filter(
                                  (item) => item.type === "snippet",
                                ).length
                              }
                              )
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </div>
                    </div>
                    <AccordionContent className="pt-0">
                      <KnowledgeSection
                        title=""
                        subtitle=""
                        items={orgItems}
                        isLoading={false}
                        activeFilter={orgFilter}
                        agentId={null}
                        showAddButton={false}
                        showRefreshButton={false}
                        onDelete={handleDelete}
                        onEdit={refreshKnowledge}
                        onRefresh={refreshKnowledge}
                      />
                    </AccordionContent>
                  </AccordionItem>

                  {/* Agent-specific knowledge section */}
                  {agentsWithItems.length > 0 && (
                    <AccordionItem
                      value="agent-specific-knowledge"
                      className="border-0"
                    >
                      <div
                        className="flex items-center justify-between py-4 cursor-pointer"
                        onClick={() =>
                          toggleSection("agent-specific-knowledge")
                        }
                      >
                        <div className="flex items-center gap-3">
                          <ChevronRight
                            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                              isOpen("agent-specific-knowledge")
                                ? "rotate-90"
                                : ""
                            }`}
                          />
                          <div className="text-left">
                            <h3 className="text-base font-medium">
                              Agent-specific knowledge
                            </h3>
                            <p className="text-sm text-muted-foreground font-normal">
                              Knowledge items assigned to specific agents
                            </p>
                          </div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <Tabs
                            value={agentFilter}
                            onValueChange={(value) =>
                              setAgentFilter(value as FilterType)
                            }
                          >
                            <TabsList className="h-8">
                              <TabsTrigger
                                value="all"
                                className="text-xs px-2 py-1"
                              >
                                <BookOpen className="mr-1 h-3 w-3" />
                                All ({agentItems.length})
                              </TabsTrigger>
                              <TabsTrigger
                                value="links"
                                className="text-xs px-2 py-1"
                              >
                                <LinkIcon className="mr-1 h-3 w-3" />
                                Links (
                                {
                                  agentItems.filter(
                                    (item) => item.type === "link",
                                  ).length
                                }
                                )
                              </TabsTrigger>
                              <TabsTrigger
                                value="files"
                                className="text-xs px-2 py-1"
                              >
                                <File className="mr-1 h-3 w-3" />
                                Files (
                                {
                                  agentItems.filter(
                                    (item) => item.type === "file",
                                  ).length
                                }
                                )
                              </TabsTrigger>
                              <TabsTrigger
                                value="snippets"
                                className="text-xs px-2 py-1"
                              >
                                <FileText className="mr-1 h-3 w-3" />
                                Snippets (
                                {
                                  agentItems.filter(
                                    (item) => item.type === "snippet",
                                  ).length
                                }
                                )
                              </TabsTrigger>
                            </TabsList>
                          </Tabs>
                        </div>
                      </div>
                      <AccordionContent className="pt-0">
                        <div className="space-y-6">
                          {agentsWithItems.map((agent) => {
                            const agentKnowledgeItems =
                              agentItemsGrouped[agent.id];
                            return (
                              <div key={agent.id} className="space-y-3">
                                {/* Agent name header */}
                                <h4 className="text-sm font-medium text-muted-foreground">
                                  {agent.agent_name
                                    ? `${agent.agent_name} • ${agent.title}`
                                    : agent.title}
                                  {agent.type === "voice" && (
                                    <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                      Voice Agent
                                    </span>
                                  )}
                                </h4>
                                {/* Knowledge items for this agent */}
                                <KnowledgeSection
                                  title=""
                                  subtitle=""
                                  items={agentKnowledgeItems}
                                  isLoading={false}
                                  activeFilter={agentFilter}
                                  agentId={agent.id}
                                  showAddButton={false}
                                  showRefreshButton={false}
                                  showEmptyState={false}
                                  onDelete={handleDelete}
                                  onEdit={refreshKnowledge}
                                  onRefresh={refreshKnowledge}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                </Accordion>

                {/* Empty state when no knowledge items exist */}
                {totalKnowledgeItemsCount === 0 && (
                  <EmptyState
                    icon={BookOpen}
                    title="No knowledge items yet"
                    description="Add your first knowledge item using the 'Add Knowledge' button above."
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default KnowledgeBase;
